import { type CollectionVariantRef, SubFormType } from '@ikegps/core-api';
import { FormModelFieldNestedList } from '../form/nested.list/form.nested.list';
import { FormModelFieldSelectList } from '../form/form.select.list';
import type { FormModel } from '../form/form';
import { CollectionModelBrightnessCorrectionState } from './brightnessCorrection/brightnessCorrection.state';
import { CollectionModelBase } from './collection.base';
import type { CollectionModelField } from './collection.field';
import { type IkeCollectionLoader, IkeCollectionLoaderFromState } from './collection.loader';
import { CollectionModelState } from './collection.state';
import { CollectionModelCropParameterState } from './cropParameter/cropParameter.state';
import { CollectionModelDrawableState } from './drawable/drawable.state';
import type { CollectionModelFieldSubForm } from './fields/form.field';
import type { CollectionModelFieldNestedList } from './fields/nested.list.field';
import { CaptureProviderHelpers } from './fields/provider/capture.provider';
import { CollectionProviderHelpers } from './fields/provider/collection.provider';
import type { CollectionModelFieldSelectList } from './fields/select.list.field';
import { CollectionModelImageVisibilityState } from './imageVisibility/imageVisibility.state';

/**
 * This class is a proxy to store all its data inside of the state.
 *
 * This is done to allow a easy place to record all changes to the Collection,
 * which can be used for editing, undo/redo stacks.
 *
 * **Do not** add any properties directly to this class
 */
export class CollectionModel extends CollectionModelBase {
	brightnessCorrectionState: { [captureId: string]: CollectionModelBrightnessCorrectionState } = {};
	cropParameterState: { [captureId: string]: CollectionModelCropParameterState } = {};
	imageVisibilityState: { [captureId: string]: CollectionModelImageVisibilityState } = {};

	/** Collection schema used by this form */
	override schema: FormModel;
	loader: IkeCollectionLoader;

	constructor(form: FormModel, loader: IkeCollectionLoader) {
		super(null, null, form);
		this.loader = loader;
		this._state = new CollectionModelState(this.loader);
	}

	static createLoader() {
		return new IkeCollectionLoaderFromState();
	}

	/** State proxy which can be used to track edits */
	private proxyState: CollectionModelState | null = null;
	private _state: CollectionModelState;

	get state(): CollectionModelState {
		if (this.isEditing && this.proxyState) {
			return this.proxyState;
		}
		return this._state;
	}

	get id(): string {
		return this.state.id;
	}
	override get instanceId() {
		return this.state.instanceId;
	}
	get uuid(): string {
		return this.state.uuid;
	}
	get revision(): string {
		return this.state.revision;
	}
	get jobId(): string {
		return this.state.jobId;
	}
	get updatedAt(): Date {
		return this.state.updatedAt;
	}
	get updatedBy(): string {
		return this.state.updatedBy;
	}
	get createdAt(): Date {
		return this.state.createdAt;
	}
	get collectedBy(): string {
		return this.state.collectedBy;
	}
	get collectedWith(): string {
		return this.state.collectedWith;
	}
	get assignedTo(): string {
		return this.state.assignedTo;
	}
	get variantType(): string | undefined {
		return this.state.variantType;
	}
	get variantTypeName(): string | undefined {
		return this.state.variantTypeName;
	}
	get variantForUuid(): string | undefined {
		return this.state.variantForUuid;
	}
	get variants(): CollectionVariantRef[] | undefined {
		return this.state.variants;
	}

	/**
	 * @deprecated Use {@link getCollectedAt} instead.
	 */
	get collectedAt(): Date {
		if (this.state.collectedAt != null) {
			return this.state.collectedAt;
		}

		const captureIds = CaptureProviderHelpers.getCaptureIds(this);
		let earliestDate: string = null;
		for (const captureId of captureIds) {
			const fetchable = this.loader.Capture.getById(captureId);

			// Only calculate when all captures are loaded
			if (!fetchable.hasFetched) {
				return null;
			}

			const capture = fetchable.value;
			if (capture.collectedAt == null) {
				continue;
			}

			if (earliestDate == null || capture.collectedAt.iso < earliestDate) {
				earliestDate = capture.collectedAt.iso;
			}
		}

		return earliestDate != null ? new Date(earliestDate) : null;
	}

	async getCollectedAt(): Promise<Date | null> {
		if (this.state.collectedAt != null) {
			return this.state.collectedAt;
		}

		const captureIds = CaptureProviderHelpers.getCaptureIds(this);
		let earliestDate: string | null = null;
		for (const captureId of captureIds) {
			const fetchable = this.loader.Capture.getById(captureId);
			const capture = await fetchable.fetch();
			if (capture.collectedAt == null) {
				continue;
			}

			if (earliestDate == null || capture.collectedAt.iso < earliestDate) {
				earliestDate = capture.collectedAt.iso;
			}
		}

		return earliestDate != null ? new Date(earliestDate) : null;
	}

	/**
	 * Look up the collection status using the status field from the metadata.
	 *
	 * If the collection does not have a status field, no field will be returned.
	 */
	get status(): CollectionModelFieldNestedList | null {
		const statusField = this.schema.metadata.getStatusField();
		if (statusField == null) {
			return null;
		}
		return this.getField<CollectionModelFieldNestedList>(statusField.id);
	}

	/**
	 * Look up the collection map color using the map color field from the metadata.
	 * If the collection does not have a map color field, no field will be returned.
	 */
	get mapMarkerColor(): string | null {
		const mapColorField = this.schema.metadata.getMapColorField();
		if (mapColorField == null) {
			return null;
		}
		let color: string | null = null;

		if (mapColorField instanceof FormModelFieldSelectList) {
			color = mapColorField.options[0]?.color ?? null;
			const field = this.getField<CollectionModelFieldSelectList>(mapColorField.id);
			if (field) {
				mapColorField.options.forEach((option) => {
					if (option.value === field.value) {
						color = option.color ?? null;
					}
				});
			}
		} else if (mapColorField instanceof FormModelFieldNestedList) {
			const field = this.getField<CollectionModelFieldNestedList>(mapColorField.id);
			color = '#a0aec0';
			if (field) {
				if (field.hasValue()) {
					for (const value of mapColorField.options.values) {
						if (value.value === field.value[0]) {
							color = value.color ?? null;
						}
					}
				}
			}
		}
		return color;
	}

	get spanSubForm(): CollectionModelFieldSubForm | null {
		const spanSubform = this.schema.getTypedSubForm(SubFormType.SPAN);
		if (!spanSubform) {
			return null;
		}

		return this.getField<CollectionModelFieldSubForm>(spanSubform.id);
	}

	/**
	 * Create a new proxy state layer, this can be used to track changes to a collection.
	 * @returns Created proxy state
	 */
	beginEdit<T extends CollectionModelState>(editState: (state: CollectionModelState) => T): T {
		const newState = editState(this._state);
		this.proxyState = newState;
		return newState;
	}

	/**
	 * Remove the proxy state layer, this will undo any changes made to a collection
	 */
	finishEdit() {
		this.proxyState = null;
	}

	get isEditing() {
		return this.proxyState != null;
	}

	get isDirty(): boolean {
		return this.proxyState?.isDirty ?? false;
	}

	get collection(): CollectionModel {
		return this;
	}

	/**
	 * Get the time the collection was created, using the collectedAt if it exists otherwise the time it was uploaded
	 *
	 * @deprecated Use {@link getRecordedAt} instead.
	 */
	get recordedAt(): Date {
		return this.collectedAt == null ? this.createdAt : this.collectedAt;
	}

	async getRecordedAt(): Promise<Date> {
		const collectedAt = await this.getCollectedAt();
		return collectedAt ?? this.createdAt;
	}

	getDrawableState(captureId: string): CollectionModelDrawableState {
		if (!this.state.drawableStates.has(captureId)) {
			this.state.drawableStates.set(captureId, new CollectionModelDrawableState(this, captureId));
		}
		const drawableState = this.state.drawableStates.get(captureId);
		if (!drawableState) {
			throw new Error(`Unable to get drawable state for capture ${captureId}`);
		}
		return drawableState;
	}

	/**
	 * Seat each base-offset marker's label at its computed marker position.
	 *
	 * A base-offset marker's label has no meaningful (x,y) of its own — it belongs at the marker,
	 * whose position is derived from the capture geometry ({@link CollectionModelDrawableMarkerOffset.y}).
	 * When a collection is built from relayed measurements (variant creation), the label is seeded at
	 * (0,0) during deserialize because the capture isn't loaded yet, and nothing recomputes it. Call this
	 * AFTER {@link ready} so the geometry is available; the computed y is then persisted by the writer.
	 * See UFO-1060.
	 */
	positionBaseOffsetLabels(): void {
		for (const drawableState of this.state.drawableStates.values()) {
			const offset = drawableState.baseOffset;
			if (offset != null && drawableState.hasDrawable(offset.labelId)) {
				offset.label.move(offset.label.x, offset.y);
			}
		}
	}

	getImageVisibilityState(captureId: string): CollectionModelImageVisibilityState {
		let existing = this.imageVisibilityState[captureId];
		if (existing == null) {
			existing = this.imageVisibilityState[captureId] = new CollectionModelImageVisibilityState(this, captureId);
		}
		return existing;
	}

	getBrightnessCorrectionState(captureId: string): CollectionModelBrightnessCorrectionState {
		let existing = this.brightnessCorrectionState[captureId];
		if (existing == null) {
			existing = this.brightnessCorrectionState[captureId] = new CollectionModelBrightnessCorrectionState(
				this,
				captureId
			);
		}
		return existing;
	}

	getCropParameterState(captureId: string): CollectionModelCropParameterState {
		let existing = this.cropParameterState[captureId];
		if (existing == null) {
			existing = this.cropParameterState[captureId] = new CollectionModelCropParameterState(this, captureId);
		}
		return existing;
	}

	private _fieldCache: { [fieldId: string]: CollectionModelField } = {};
	private _fieldCacheCount = 0;
	/**
	 * Find a field using its unique field Id
	 * Used for quickly looking up field links.
	 *
	 * @param fieldId Unique field Id including subformId
	 * @returns collection field if it exists
	 */
	findField<T extends CollectionModelField>(fieldId: string): T | null {
		let existing = this._fieldCache[fieldId];
		if (existing !== undefined) {
			return existing as T;
		}

		let output: T = null;
		this.forEachField((field) => {
			if (field.id === fieldId) {
				output = field as T;
				return false; // abort the search
			}
			return true;
		});

		// Only cache positive hits. Null results are NOT cached because new subform
		// instances can be added after the first lookup (e.g. during span copy), which
		// would leave a stale null entry that prevents the field from ever being found.
		if (output != null) {
			this._fieldCache[fieldId] = output;
			this._fieldCacheCount++;
			// Paranoia incase the cache gets too large
			if (this._fieldCacheCount > 1000) {
				this._fieldCacheCount = 0;
				this._fieldCache = {};
			}
		}
		return output;
	}

	/**
	 * Collections are not loaded with all their information.
	 *
	 * Wait for all the linked data to load in
	 *
	 * If any object in the returned list is null, it means the collection failed to load something.
	 *
	 * @return List of objects that were loaded
	 */
	async ready(): Promise<object[]> {
		const captureIds = CaptureProviderHelpers.getCaptureIds(this);
		const collectionIds = CollectionProviderHelpers.getCollectionIds(this);
		const fetchables: Promise<object>[] = [];

		const captures = await Promise.all(captureIds.map((id) => this.loader.Capture.getById(id).fetch()));

		for (let i = 0; i < captures.length; i++) {
			const capture = captures[i];
			if (capture == null) {
				throw new Error('Unable to load capture: ' + captureIds[i]);
			}
			if (capture.calibrationid == null) {
				continue;
			}

			fetchables.push(this.loader.Calibration.getById(capture.calibrationid).fetch());
		}

		for (const collectionId of collectionIds) {
			fetchables.push(this.loader.CollectionSummary.getById(collectionId).fetch());
		}

		// Note: We could fetch all linked collection models here, but if we did, it would
		// recursively load all of the linked collections, which may trigger an infinite loop or
		// an unnecessarily large number of fetches.
		// So if you need to load linked collection models, use `CollectionModelState.getCollectionModelAsync()`
		// or `CollectionModelFieldVector.getLinkedCollection()`.

		return await Promise.all(fetchables);
	}
}
